import { JSX, Suspense } from 'react';
import { RequireRole } from '../auth';
import { CenteredLoader, RouteErrorFallback } from '../components';
import { ROUTES } from '../config';
import RootLayout from '../RootLayout';
import { lazyWithRetry } from './lazyWithRetry';

// Pages are lazily imported so each becomes its own chunk fetched on
// navigation, keeping the initial bundle small (#159). The shared Suspense
// boundary lives in RootLayout, around the router Outlet. `lazyWithRetry`
// recovers from stale chunk hashes after a deploy (#160 review).
const LandingPage = lazyWithRetry(() => import('../pages/LandingPage'));
const Sunday = lazyWithRetry(() => import('../pages/Sunday'));
const ServiceDetail = lazyWithRetry(() => import('../pages/ServiceDetail'));
const FreshBread = lazyWithRetry(() => import('../pages/FreshBread'));
const EquippingHour = lazyWithRetry(() => import('../pages/EquippingHour'));
const PodcastDetail = lazyWithRetry(() => import('../pages/PodcastDetail'));
const Settings = lazyWithRetry(() => import('../pages/Settings'));
const Planning = lazyWithRetry(() => import('../pages/Planning'));
const SongLibrary = lazyWithRetry(() => import('../pages/SongLibrary'));
const SongPage = lazyWithRetry(() => import('../pages/SongPage'));
const People = lazyWithRetry(() => import('../pages/People'));
const SlideConfig = lazyWithRetry(() => import('../pages/SlideConfig'));
const MasterPlan = lazyWithRetry(() => import('../pages/MasterPlan'));
const PlanEditor = lazyWithRetry(() => import('../pages/PlanEditor'));
const Bulletin = lazyWithRetry(() => import('../pages/Bulletin'));
const Handout = lazyWithRetry(() => import('../pages/Handout'));

type TRoute = {
  path: string;
  element: JSX.Element;
  errorElement?: JSX.Element;
  children?: TRoute[];
};

type TRoutes = TRoute[];

const routes: TRoutes = [
  {
    path: ROUTES.LANDING,
    element: <RootLayout />,
    errorElement: <RouteErrorFallback />,
    // Each child carries its own errorElement so a failure inside a route
    // (e.g. a stale lazy-chunk load after a deploy) renders within AppShell.Main
    // and keeps the RootLayout shell — header, navbar, and active audio player —
    // alive, instead of bubbling up and tearing down the whole layout (#160 review).
    children: [
      {
        path: ROUTES.LANDING,
        element: <LandingPage />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.SUNDAY,
        element: <Sunday />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.SERVICE_DETAIL,
        element: <ServiceDetail />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.FRESH_BREAD,
        element: <FreshBread />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.EQUIPPING_HOUR,
        element: <EquippingHour />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.PODCAST_DETAIL,
        element: <PodcastDetail />,
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.PLANNING,
        element: (
          <RequireRole capability="planServices">
            <Planning />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        // Declared before PLANNING_EDITOR (`/planning/:date`): React Router v6
        // ranks static segments above dynamic ones regardless of order, but
        // ordering it first keeps the intent legible.
        path: ROUTES.PLANNING_SONGS,
        element: (
          <RequireRole capability="planServices">
            <SongLibrary />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        // `/planning/songs/:songId`. Its two segments rank above the one-segment
        // `/planning/:date` editor route, so a song id never lands on the plan
        // editor.
        path: ROUTES.PLANNING_SONG,
        element: (
          <RequireRole capability="planServices">
            <SongPage />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.PLANNING_PEOPLE,
        element: (
          <RequireRole capability="planServices">
            <People />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        // `/planning/slides`. Static, so it ranks above the `/planning/:date`
        // editor route and a plan date never lands on the slide config page.
        path: ROUTES.PLANNING_SLIDES,
        element: (
          <RequireRole capability="planServices">
            <SlideConfig />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        // `/planning/master-plan`. Static, so it ranks above the `/planning/:date`
        // editor route and never lands on the plan editor.
        path: ROUTES.PLANNING_MASTER_PLAN,
        element: (
          <RequireRole capability="planServices">
            <MasterPlan />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.PLANNING_EDITOR,
        element: (
          <RequireRole capability="planServices">
            <PlanEditor />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
      {
        path: ROUTES.SETTINGS,
        element: (
          <RequireRole capability="viewSettings">
            <Settings />
          </RequireRole>
        ),
        errorElement: <RouteErrorFallback />,
      },
    ],
  },
  {
    // Public bulletin permalink — deliberately OUTSIDE RootLayout: no header,
    // navbar, or audio player, so the page reads and prints like a document.
    path: ROUTES.BULLETIN,
    element: (
      <Suspense fallback={<CenteredLoader size="lg" />}>
        <Bulletin />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    // Public handout permalink, OUTSIDE RootLayout like the bulletin, so it
    // reads and prints like a document.
    path: ROUTES.HANDOUT,
    element: (
      <Suspense fallback={<CenteredLoader size="lg" />}>
        <Handout />
      </Suspense>
    ),
    errorElement: <RouteErrorFallback />,
  },
];

export default routes;
